import React, { useCallback, useEffect, useState } from 'react';
import { evidenceFilesApi } from '../../utils/api';
import EvidenceDropTarget, { namedFileFor } from './EvidenceDropTarget';
import { fmtDate, fmtDateTime } from './dates';
import UploadReceipt from './UploadReceipt';

// PRD-030 WU-11 — delivery status cards, built to the ratified mockup
// (rev 2): one card per Outcome answering the client's six questions in
// order — what we're verifying (title), what success means, how we're
// tracking / the final result, what evidence we checked (with
// timestamps), where it came from, and the status. Time-aware status is
// SERVER-computed (F7a; the UI never derives its own) and identical for
// Agency and Client (F7 full visibility). Plain English only.

const STATUS_CHIPS = {
  on_track: { label: 'On track', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  behind: { label: 'Behind', cls: 'text-amber-800 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  not_measurable_yet: { label: 'Not measurable yet', cls: 'text-gray-600 bg-gray-50 border-gray-200', dot: 'bg-gray-400' },
  awaiting_evidence: { label: 'Awaiting evidence', cls: 'text-gray-600 bg-gray-50 border-gray-200', dot: 'bg-gray-400' },
  checking_now: { label: 'Checking now', cls: 'text-sky-700 bg-sky-50 border-sky-200', dot: 'bg-sky-500' },
  delivered_confirmed: { label: 'Delivered', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  delivered_pending_confirmation: { label: 'Delivered — pending agency confirmation', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  not_delivered: { label: 'Not delivered', cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
  needs_human_review: { label: 'Needs human review', cls: 'text-amber-800 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  // WU-A′ status coherence: evidence is in but the newest check died —
  // named plainly, never masked by empty-state copy.
  check_failed: { label: "Check didn't finish", cls: 'text-amber-800 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
};

// The stage a failed check died in, in the customer's words (WU-A′ §2).
// Internal stage names never reach the screen.
const FAILED_STAGE_PHRASES = {
  queued: 'before it could start',
  planning: 'while planning the check',
  collecting: 'while reading the evidence files',
  judging: 'while judging the evidence',
};

// WU-C — which primary verb an Outcome gets, as a pure function so the matrix
// is testable (QA round 2, F6: this predicate regressed once already and a
// single bench screenshot is thin protection).
//   evidence missing  → the card offers "Add evidence" (and the action row
//                       suppresses "Submit evidence for checking")
//   evidence present  → the action row keeps Submit
// A check already running, or a delivered Outcome, offers neither: there is
// nothing to add. The client never sees either verb.
export const NO_CTA_STATUSES = ['checking_now', 'delivered_confirmed', 'delivered_pending_confirmation'];

export function evidenceCtaFor({ status, hasEvidence, isAgency }) {
  return Boolean(isAgency) && !hasEvidence && !NO_CTA_STATUSES.includes(status);
}

function StatusChip({ status }) {
  const chip = STATUS_CHIPS[status] || STATUS_CHIPS.awaiting_evidence;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${chip.cls}`}>
      <span className={`w-2 h-2 rounded-full ${chip.dot}`} />
      {chip.label}
    </span>
  );
}

function QA({ q, children }) {
  if (!children) return null;
  return (
    <>
      <div className="text-[11px] uppercase tracking-wide font-bold text-gray-600 pt-0.5">{q}</div>
      <div className="text-[13px] text-gray-800">{children}</div>
    </>
  );
}

const AGENT = <span className="font-bold text-violet-600">Verdict Agent</span>;

// The latest agent note worth surfacing for this outcome: a needs-review
// explanation, a final-result rationale, or the newest reasoning line.
function latestReasoning(evidence, wantVerdict) {
  const rows = (evidence || [])
    .filter((e) => e.reasoning && (!wantVerdict || e.verdict === wantVerdict))
    .sort((a, b) => new Date(b.fetched_at || 0) - new Date(a.fetched_at || 0));
  return rows[0] || null;
}

function dueLine(row, delivery) {
  if (!row.activeTo) return null;
  if (delivery.window_closed) return <>closed <b className="text-gray-600">{fmtDate(row.activeTo, { year: false })}</b></>;
  return (
    <>
      due <b className="text-gray-600">{fmtDate(row.activeTo, { year: false })}</b>
      {delivery.days_left != null && <> · {delivery.days_left} day{delivery.days_left === 1 ? '' : 's'} left</>}
    </>
  );
}

function Card({ row, files, renderActions, isAgency, agreementId, allOutcomes, onEvidenceAdded }) {
  // The upload's own banners live HERE: a successful upload unmounts the drop
  // target, and state inside it would vanish with the nudge (QA finding 2).
  const [uploadNotices, setUploadNotices] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  // Same reason as its siblings above: a successful upload unmounts the drop
  // target, so the acknowledgment cannot live inside it.
  const [receipt, setReceipt] = useState(null);
  const delivery = row.delivery;
  if (!delivery) return null;
  const status = delivery.status;

  const evidenceFiles = files || [];
  const target = row.sellQuantity != null && row.sellUnitNoun
    ? `${Number(row.sellQuantity).toLocaleString()} ${row.sellUnitNoun}${Number(row.sellQuantity) === 1 ? '' : 's'}`
    : null;

  // WU-C — the Outcome's own evidence decides which verb is honest: with
  // nothing uploaded, "Submit evidence for checking" would spend a run that
  // short-circuits to needs_review, so the card offers "Add evidence" instead.
  // evidence_present is SERVER-computed on every status (it counts
  // Agreement-wide uploads this grouping cannot see); the local list is the
  // fallback for a payload that predates it.
  const hasEvidence = Boolean(delivery.evidence_present) || evidenceFiles.length > 0;
  // Every state where evidence is the missing ingredient offers the verb —
  // including needs_human_review, which is exactly where an empty-manifest
  // check leaves an Outcome (QA finding 1: gating Submit on a narrower set
  // than this stranded those cards with no way to add evidence at all).
  const evidenceCtaShown = evidenceCtaFor({ status, hasEvidence, isAgency });
  const namedFile = namedFileFor(row);

  const reviewNote = status === 'needs_human_review' ? latestReasoning(row.evidence, 'needs_review') : null;
  const resultNote = ['delivered_confirmed', 'delivered_pending_confirmation', 'not_delivered'].includes(status)
    ? latestReasoning(row.evidence)
    : null;

  return (
    <div className="card overflow-hidden mb-4" id={`delivery-outcome-${row.outcomeId}`}
      style={{ scrollMarginTop: 'calc(var(--sticky-offset, 0px) + 12px)' }}
      data-testid={`status-card-${status}`}>
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 flex-wrap">
        <StatusChip status={status} />
        <span className="text-[15px] font-semibold text-gray-900">{row.name}</span>
        <span className="flex-1" />
        <span className="text-[11.5px] text-gray-600 tabular-nums">{dueLine(row, delivery)}</span>
      </div>
      <div className="px-4 py-4">
        <div className="grid grid-cols-[168px,1fr] gap-x-4 gap-y-2.5">
          <QA q="What success means">{delivery.success_criteria}</QA>

          {status === 'not_measurable_yet' && (
            <QA q="Status">
              The measurement window {row.activeFrom ? <>opens {fmtDate(row.activeFrom, { year: false })}</> : 'has not opened yet'}.
              We'll start checking as evidence arrives.
            </QA>
          )}
          {status === 'awaiting_evidence' && !hasEvidence && (
            <QA q="Status">
              No evidence yet{target ? <> against the {target} target</> : ''}.
              {isAgency ? <> Add {namedFile || 'it'} below and checking starts on its own.</> : null}
            </QA>
          )}
          {status === 'awaiting_evidence' && (delivery.evidence_present || evidenceFiles.length > 0) && (
            <QA q="Status">
              The evidence is in — waiting for a check to run. Checks start automatically{isAgency ? '; the Submit evidence button below starts one at once' : ''}.
            </QA>
          )}
          {status === 'checking_now' && (
            <QA q="Status">A check is under way. Results appear here the moment it finishes.</QA>
          )}
          {status === 'check_failed' && (
            <QA q="Status">
              The evidence is in, but the last check stopped {FAILED_STAGE_PHRASES[delivery.failed_check?.stage] || 'before it finished'}
              {delivery.failed_check?.finished_at ? <> ({fmtDateTime(delivery.failed_check.finished_at)})</> : null}.
              {delivery.failed_check?.error_code === 'daily_cap'
                ? ' Checking resumes automatically next period.'
                : isAgency
                  ? ' Retry the check below — the evidence stays in place.'
                  : ' The agency can retry it — the evidence stays in place.'}
            </QA>
          )}

          {reviewNote && (
            <QA q="Why a person should look">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-[12.5px] text-gray-600">{reviewNote.reasoning}</div>
            </QA>
          )}
          {resultNote && (
            <QA q="What we did">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-[12.5px] text-gray-600">{resultNote.reasoning}</div>
            </QA>
          )}

          {(() => {
            const latest = (row.evidence || [])
              .filter(e => e.measurements?.length > 0)
              .sort((a, b) => new Date(b.fetched_at || 0) - new Date(a.fetched_at || 0))[0];
            if (!latest) return null;
            return (
              <QA q="How we're tracking">
                <table className="w-full text-[12.5px] border-collapse">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-600">
                      <th className="pb-1 pr-3 font-bold">Promise</th>
                      <th className="pb-1 pr-3 font-bold">Target</th>
                      <th className="pb-1 pr-3 font-bold">Observed</th>
                      <th className="pb-1 font-bold w-6" />
                    </tr>
                  </thead>
                  <tbody>
                    {latest.measurements.map((m, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="py-1.5 pr-3 text-gray-800">{m.statement}</td>
                        <td className="py-1.5 pr-3 text-gray-600 tabular-nums">{m.target_value}</td>
                        <td className="py-1.5 pr-3 text-gray-800 tabular-nums font-semibold">{m.observed_value}</td>
                        <td className="py-1.5 text-right">
                          {m.met
                            ? <span className="text-emerald-600 font-bold text-[11px]">Met</span>
                            : <span className="text-red-600 font-bold text-[11px]">Not met</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </QA>
            );
          })()}

          {evidenceFiles.length > 0 && (
            <QA q="Evidence we checked">
              {evidenceFiles.map((f) => (
                <div key={f.id} className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[9.5px] font-extrabold tracking-wide text-gray-600 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                    {(f.mime_type || '').includes('csv') ? 'CSV' : (f.mime_type || '').includes('sheet') ? 'XLSX' : (f.mime_type || '').includes('png') ? 'PNG' : (f.mime_type || '').includes('jpeg') ? 'JPG' : (f.mime_type || '').includes('json') ? 'JSON' : 'FILE'}
                  </span>
                  <span>{f.filename}</span>
                  <span className="text-[11px] text-gray-600 tabular-nums">· uploaded {fmtDateTime(f.created_at)}</span>
                </div>
              ))}
            </QA>
          )}

          <QA q="Where it came from">{delivery.evidence_source}</QA>
        </div>

        {evidenceCtaShown && (
          <EvidenceDropTarget
            agreementId={agreementId}
            outcome={row}
            allOutcomes={allOutcomes}
            onUploaded={onEvidenceAdded}
            onNotice={setUploadNotices}
            onError={setUploadErrors}
            onReceipt={setReceipt}
          />
        )}
        <UploadReceipt receipt={receipt} onDismiss={() => setReceipt(null)} compact />
        {uploadErrors.length > 0 && (
          <div className="mt-1.5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-[12px] px-3 py-2" data-testid="drop-error">
            <div className="flex-1">{uploadErrors.map((e, i) => <div key={i}>{e}</div>)}</div>
            <button type="button" onClick={() => setUploadErrors([])} className="text-[11.5px] font-semibold text-red-700">Dismiss</button>
          </div>
        )}
        {uploadNotices.length > 0 && (
          <div className="mt-1.5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-[12px] px-3 py-2" data-testid="drop-notice">
            <div className="flex-1">{uploadNotices.map((n, i) => <div key={i}>{n}</div>)}</div>
            <button type="button" onClick={() => setUploadNotices([])} className="text-[11.5px] font-semibold text-amber-800">Dismiss</button>
          </div>
        )}
        {renderActions && <div className="mt-3">{renderActions(row, { evidenceCtaShown })}</div>}
        <div className="text-[11.5px] text-gray-600 mt-3 pt-2.5 border-t border-gray-100">
          {status === 'needs_human_review' && delivery.last_checked_at && (
            <>Flagged {fmtDateTime(delivery.last_checked_at)} by {AGENT}.</>
          )}
          {status === 'check_failed' && (
            <>Stopped {delivery.failed_check?.finished_at ? fmtDateTime(delivery.failed_check.finished_at) : 'before finishing'} · the evidence stays in place.</>
          )}
          {status !== 'needs_human_review' && status !== 'check_failed' && delivery.last_checked_at && (
            <>Last checked {fmtDateTime(delivery.last_checked_at)} by {AGENT} · checks run automatically when new evidence arrives.</>
          )}
          {status !== 'check_failed' && !delivery.last_checked_at && <>Nothing checked yet — checks run automatically when evidence arrives.</>}
        </div>
      </div>
    </div>
  );
}

export default function DeliveryStatusCards({ agreementId, rows, renderActions, isAgency, onEvidenceAdded }) {
  const [files, setFiles] = useState([]);
  const loadFiles = useCallback(
    () => evidenceFilesApi.list(agreementId)
      .then((res) => setFiles(res.data.evidence_files || []))
      .catch(() => {}),
    [agreementId]
  );
  useEffect(() => { loadFiles(); }, [loadFiles]);
  // An upload auto-triggers a check (F6), so the card refreshes its own files
  // AND the page's status/run state — the state moves under the user without
  // a manual reload.
  const afterUpload = useCallback(async () => {
    await loadFiles();
    if (onEvidenceAdded) await onEvidenceAdded();
  }, [loadFiles, onEvidenceAdded]);

  const deliverableToOutcome = new Map();
  for (const o of rows) for (const d of o.deliverables || []) deliverableToOutcome.set(d.id, o.outcomeId);
  const filesByOutcome = new Map();
  for (const f of files) {
    const oid = f.deliverable_id ? deliverableToOutcome.get(f.deliverable_id) : null;
    if (!oid) continue;
    if (!filesByOutcome.has(oid)) filesByOutcome.set(oid, []);
    filesByOutcome.get(oid).push(f);
  }

  return (
    <div>
      {rows.every((row) => !row.delivery) ? (
        <div className="card p-6 text-center text-gray-600 text-[13px]" data-testid="status-unavailable">
          Delivery status isn't available right now — refresh the page to try again.
        </div>
      ) : (
        rows.map((row) => (
          <Card key={row.outcomeId} row={row} files={filesByOutcome.get(row.outcomeId)} renderActions={renderActions}
            isAgency={isAgency} agreementId={agreementId} allOutcomes={rows} onEvidenceAdded={afterUpload} />
        ))
      )}
    </div>
  );
}
